import { Alert, Button, Chip, makeStyles } from '@paintscout/ui'
import type { Theme } from '@paintscout/ui/styles'
import { brandPalette, secondaryPalette, semanticPalette } from '@paintscout/ui/src/styles/palette'
import { useClientOptions, useUser } from '@paintscout/ui-business'
import { formatCurrency, getQuoteLoanApplication } from '@paintscout/util/builder'
import wisetackStatusMapping from '@paintscout/util/util/wisetackStatusMapping/wisetackStatusMapping'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import LockIcon from '@material-ui/icons/Lock'
import ScheduleIcon from '@material-ui/icons/Schedule'
import SendIcon from '@material-ui/icons/Send'
import classnames from 'classnames'
import React, { useState } from 'react'
import type { LoanApplicationStatus } from '@paintscout/wisetack'
import { useQuote } from '../../context'
import { getWisetackAlerts } from '../getWisetackAlerts'
import { useWisetack } from '../useWisetack'
import { useWisetackSection } from '../useWisetackSection'

/**
 * The chip tone for a loan status. The chip reuses PaymentStatusBadge's palette so the financing
 * status reads the same as a payment status in PaymentsTableNext: success for approved/funded,
 * danger for declined/blocked, warning for expired/canceled, neutral for everything in progress.
 */
type ChipTone = 'success' | 'danger' | 'warning' | 'neutral'

function getChipTone(status?: LoanApplicationStatus): ChipTone {
  switch (status) {
    case 'SETTLED':
    case 'LOAN CONFIRMED':
    case 'LOAN TERMS ACCEPTED':
      return 'success'
    case 'DECLINED':
    case 'ACTIONS REQUIRED':
      return 'danger'
    case 'EXPIRED':
    case 'CANCELED':
      return 'warning'
    default:
      return 'neutral'
  }
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: theme.spacing(1),
        marginTop: `-${theme.spacing(1)}px`,
        padding: theme.spacing(0, 1.5, 1.5),
        // The merchant's call to action is app chrome, not part of the document.
        '@media print': {
          display: 'none'
        }
      },
      // The status reads as a centered pill — see getChipTone for the color mapping.
      chipRow: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5)
      },
      chip: {
        height: 'auto',
        padding: theme.spacing(0.5, 0.75),
        fontSize: theme.typography.pxToRem(13),
        fontWeight: theme.typography.fontWeightMedium,
        border: '1px solid',
        // The label wraps on a narrow card rather than overflowing the pill.
        '& .MuiChip-label': {
          whiteSpace: 'normal',
          textAlign: 'center'
        }
      },
      // The four tones below are copied from PaymentStatusBadge (paid / failed / pending /
      // processing) so a financing chip and a payment badge use one palette.
      chipSuccess: {
        backgroundColor: secondaryPalette['140'].C95,
        borderColor: semanticPalette.Success.S60,
        color: semanticPalette.Success.S50
      },
      chipDanger: {
        backgroundColor: semanticPalette.Danger.S95,
        borderColor: semanticPalette.Danger.S60,
        color: semanticPalette.Danger.S60
      },
      chipWarning: {
        backgroundColor: semanticPalette.Warning.S95,
        borderColor: semanticPalette.Warning.S80,
        color: semanticPalette.Warning.S70
      },
      chipNeutral: {
        backgroundColor: brandPalette.Neutral.P96,
        borderColor: brandPalette.Neutral.P80,
        color: brandPalette.Neutral.P50
      },
      chipIcon: {
        color: 'inherit',
        width: theme.typography.pxToRem(15),
        height: theme.typography.pxToRem(15)
      },
      // Alerts stay in the flow rather than behind an icon — they tell the merchant or the
      // customer that money is still owed, which is too important to hide (matches the legacy card).
      alerts: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        textAlign: 'left'
      },
      // Smaller than the legacy card's alerts — they sit inside a 360px total table, not a
      // full-width section.
      alertRoot: {
        padding: theme.spacing(1, 1.5),
        marginBottom: theme.spacing(0.5)
      },
      alertContent: {
        fontSize: theme.typography.pxToRem(13),
        lineHeight: 1.45
      },
      alertIcon: {
        '& svg': {
          width: 16,
          height: 16
        }
      },
      button: {
        // Full-width filled CTA, per the redesign mock. The dark neutral is deliberate — the
        // contained variant would paint this the client's brand color, and the mock keeps the
        // Wisetack action neutral against the branded Accept/Pay buttons. `!important` because
        // Button's own `contained` class sets the same property at the same specificity, and
        // JSS insertion order decides that tie (see Button.tsx, #21962).
        width: '100%',
        background: `${neutral.P35} !important`,
        color: theme.palette.common.white,
        '&:hover': {
          background: `${neutral.P40} !important`
        }
      }
    }
  },
  { name: 'WisetackApplicationNext' }
)

/**
 * The estimateNext replacement for WisetackApplication's grey card: a compact block that sits
 * under the promo line inside the total table, carrying the loan status chip, the alerts and
 * the apply action.
 *
 * The legacy quote view keeps WisetackApplication — this one is only rendered by TotalTableNext.
 */
export default function WisetackApplicationNext() {
  const { options } = useClientOptions()
  const { user, preferences } = useUser()
  const classes = useStyles()
  const { quote, save: saveQuote, isPresentation, isCustomerView } = useQuote()
  const { maximumLoanAmount, handleTextLoanApplication, handleGetLoanApplication } = useWisetack({
    user,
    options,
    preferences
  })
  const { showApplication } = useWisetackSection()
  const contact = quote?.contact
  const loanApplication = getQuoteLoanApplication({ contact, quote })
  const loanStatus = loanApplication?.changedStatus as LoanApplicationStatus | undefined
  const [loading, setLoading] = useState(false)

  if (!showApplication) {
    return null
  }

  const maximumAmount = loanApplication?.maximumAmount
  const approvedAmount = loanApplication?.approvedLoanAmount
  const payouts = loanApplication?.payouts

  // Merchant-facing preview (QuotePreviewDialog): render as the customer sees it, but the
  // application button must not initiate financing. (ENG-833)
  const isPreview = isPresentation && !isCustomerView

  const handleApply = async () => {
    if (isPresentation) {
      return handleGetLoanApplication(quote)
    }
    if (!!saveQuote && !quote._rev && !!quote.contact) {
      return saveQuote(quote).then(handleTextLoanApplication)
    }
    return handleTextLoanApplication(quote)
  }

  const onApply = async () => {
    setLoading(true)
    try {
      await handleApply()
    } finally {
      // A failed send (no contact, a Wisetack error) must still release the button.
      setLoading(false)
    }
  }

  const getButtonLabel = () => {
    if (!isPresentation) {
      return 'Send Application Link'
    }
    if (loanStatus === undefined) {
      return 'See options'
    }
    // Wisetack lets a declined customer apply again, so the promo and the action both stay.
    if (loanStatus === 'DECLINED') {
      return 'Apply Again'
    }
    if (
      loanStatus === 'SETTLED' ||
      (approvedAmount && approvedAmount > quote?.totals?.balance_due && (payouts?.length ?? 0) < 2)
    ) {
      return 'Apply Again'
    }
    if (loanStatus === 'LOAN TERMS ACCEPTED') {
      return 'Release Funds'
    }
    return 'Continue Application'
  }

  // The old card carried the amounts in its sales copy ("... is qualified for up to $12,000").
  // The chip folds them into the status instead — the copy itself is gone. No application yet
  // means no chip at all.
  const getChipLabel = () => {
    if (!loanStatus) {
      return null
    }
    // Wisetack's own wording puts the "can send app again" hint in brackets; the chip keeps the
    // status short and drops that hint onto its own line below.
    if (loanStatus === 'DECLINED') {
      return 'Declined'
    }
    const mapped = wisetackStatusMapping({ options, status: loanStatus, approvedLoanAmount: approvedAmount })
    const qualifiedFor =
      !approvedAmount && maximumAmount ? `Up to ${formatCurrency({ options, value: maximumAmount })}` : null

    return [mapped, qualifiedFor].filter(Boolean).join(' · ')
  }

  const getChipIcon = () => {
    switch (loanStatus) {
      case 'DECLINED':
        return <CloseIcon className={classes.chipIcon} />
      case 'ACTIONS REQUIRED':
        return <ErrorOutlineIcon className={classes.chipIcon} />
      case 'LOAN TERMS ACCEPTED':
        return <LockIcon className={classes.chipIcon} />
      case 'AUTHORIZED':
      case 'LOAN CONFIRMED':
      case 'SETTLED':
        return <CheckIcon className={classes.chipIcon} />
      default:
        return <ScheduleIcon className={classes.chipIcon} />
    }
  }

  const alerts = getWisetackAlerts({ quote, options, maximumLoanAmount, inPresentation: isPresentation })
  const hideButton = loanStatus === 'LOAN CONFIRMED'

  const chipLabel = getChipLabel()
  const chipTone = getChipTone(loanStatus)

  return (
    <div className={classes.root}>
      {chipLabel && (
        <div className={classes.chipRow}>
          <Chip
            className={classnames(classes.chip, {
              [classes.chipSuccess]: chipTone === 'success',
              [classes.chipDanger]: chipTone === 'danger',
              [classes.chipWarning]: chipTone === 'warning',
              [classes.chipNeutral]: chipTone === 'neutral'
            })}
            classes={{ icon: classes.chipIcon }}
            icon={getChipIcon()}
            label={chipLabel}
          />
        </div>
      )}
      {alerts.length > 0 && (
        <div className={classes.alerts}>
          {alerts.map((alert, index) => (
            <Alert
              key={index}
              severity="warning"
              noMargin
              classes={{
                root: classes.alertRoot,
                contentWrapper: classes.alertContent,
                iconWrapper: classes.alertIcon
              }}
            >
              {alert}
            </Alert>
          ))}
        </div>
      )}
      {!hideButton && (
        <Button
          className={classes.button}
          // In the merchant preview the button is shown for fidelity, but it must not be able to
          // initiate financing (ENG-833).
          style={{ pointerEvents: isPreview ? 'none' : 'auto' }}
          id="allow-propagation"
          fullWidth
          icon={isPreview || isCustomerView ? null : SendIcon}
          loading={loading}
          onClick={onApply}
        >
          {getButtonLabel()}
        </Button>
      )}
    </div>
  )
}
